import { useState } from 'react';
import { Tooltip } from '@bfe/moly';
import { useFm, useHybirdWallet, useParams } from '@web3/base-hooks';
import { HelpIcon, LoadingIcon } from '@bfe/moly-icons';
import { useUserStatus } from '@/hooks/useUserStatus';
import { scTrack, WEB3_EVENT } from '@web3/base-tracking';
import { useProjectData } from '@/containers/aidol/hooks';
import {
  justFormatNumber,
  appPath,
  hybirdOpen,
  WalletType,
} from '@web3/base-utils';
import { LearnModal } from '@/containers/aidol/modal';
import { useScreenType } from '@/hooks/media';
import { NoSSR } from '@web3/base-components';
import { DexTradeRules, TimeInfo } from '@/types/project';
import { UserStatus } from '@/types/user';
import cls from 'classnames';
import { DirectionrightIcon } from '@bfe/moly-icons';
import { formatAmount } from '@/utils';

import styles from './index.module.scss';

interface Props {
  web3DexProAmount: string;
  web3DexProPoints: number;
  web3BBSOLAmount: string;
  web3BBSOLPoints: number;
  spotDexProAmount: string;
  spotDexProPoints: number;
  spotBBSOLAmount: string;
  spotBBSOLPoints: number;
  pageName: string;
  projectName: string;
  chainId?: string;
  profileStyle?: boolean;
}

const bbSOLDeepLink =
  'bybitapp://open/route?targetUrl=by-mini%3A%2F%2Fweb3_applet%2Fearn%2Fstake%2Fpool_detail%3FpoolId%3D77';

export const PointsArea = (props: Props) => {
  const {
    web3DexProAmount,
    web3DexProPoints,
    web3BBSOLAmount,
    web3BBSOLPoints,
    spotDexProAmount,
    spotDexProPoints,
    spotBBSOLAmount,
    spotBBSOLPoints,
    chainId = '999999997',
    profileStyle,
    pageName,
    projectName,
  } = props;
  const { userStatus, address, email, isBindingLoading, beginBinding } =
    useUserStatus();
  const { mnemonicChainList, openLoginModal, openSpotLoginModal, walletType } =
    useHybirdWallet();
  const { locale } = useParams();
  const fm = useFm();
  const [showModal, setShowModal] = useState(false);
  const { isMobile } = useScreenType();

  return (
    <>
      <div className={styles.container}>
        <div className={styles.titleArea}>
          <div
            className={cls(styles.title, {
              [styles.profileTitle]: profileStyle,
            })}
          >
            {fm('getVotingPointsBybitWallet')}
          </div>
          <HelpIcon
            className={cls(styles.tipIcon, {
              [styles.profileTipIcon]: profileStyle,
            })}
            onClick={() => {
              scTrack(WEB3_EVENT.Web3PageView, {
                page_name: pageName,
                spmName: projectName,
                button_name: 'HowtoGetVtoingPoints',
              });
              setShowModal(true);
            }}
          />
        </div>
        <div
          className={cls(styles.content, {
            [styles.profileContent]: profileStyle,
          })}
        >
          {userStatus === UserStatus.WALLET_NOT_BINDING && (
            <div className={styles.bindingArea}>
              <div
                className={styles.binding}
                onClick={() => {
                  if (!address) {
                    openLoginModal();
                    return;
                  }

                  if (isBindingLoading) return;

                  beginBinding();
                }}
              >
                {isBindingLoading && <LoadingIcon className={styles.loading} />}
                {fm('connectBybitWeb3WalletDoublePointsLimit').replace(
                  /[!|！]/g,
                  ''
                )}
                <DirectionrightIcon className={styles.bindingArrow} />
              </div>
            </div>
          )}
          {(userStatus === UserStatus.SPOT_NOT_BINDING ||
            userStatus === UserStatus.SPOT_FINISH ||
            userStatus === UserStatus.WALLET_FINISH) && (
            <>
              {/* web3 dex */}
              <div className={styles.row}>
                <div className={styles.rowTitle}>{fm('dexProTrading')}</div>
                <div className={styles.rowDetail}>
                  {!isMobile && (
                    <>
                      <div className={styles.rowDesc}>
                        {fm('tradingVolume')}
                      </div>
                      <div className={styles.rowSymbol}>:</div>
                    </>
                  )}

                  <div className={styles.rowVolumn}>
                    {`$${formatAmount(web3DexProAmount)}`}
                  </div>
                  <div className={styles.rowLine}></div>
                  <div
                    className={styles.rowPoints}
                    onClick={async () => {
                      const chainInfo = mnemonicChainList.find(
                        (item) => +item.chainId === +chainId
                      );
                      const chainCode = chainInfo?.chainCode || 'sol';

                      const webUrl = `${window.location.origin}/${locale}/web3/dex/${chainCode}?utm_content=aidol2025`;
                      const deeplink = `bybitapp://open/route?targetUrl=by://web3/tab/index?tag=trade&swapBizType=swap&swapFromChainCode=&swapFromCoin=&swapToChainCode=${chainCode ?? ''}&swapToCoin=`;
                      scTrack(WEB3_EVENT.Web3PageView, {
                        page_name: pageName,
                        spmName: projectName,
                        button_name: 'DEXTrade',
                      });

                      hybirdOpen(deeplink, webUrl);
                    }}
                  >
                    {fm('amountPoints', {
                      amount: formatAmount(`${web3DexProPoints}`),
                    })}
                    <DirectionrightIcon color="#80F9F8" />
                  </div>
                </div>
              </div>
              {/* web3 bbsol */}
              <div
                className={cls(styles.row, styles.secondRow, {
                  [styles.profileSecondRow]: profileStyle,
                })}
              >
                <div className={styles.rowTitle}>{fm('bbSolHolding')}</div>
                <div className={styles.rowDetail}>
                  {!isMobile && (
                    <>
                      <div className={styles.rowDesc}>{fm('holding')}</div>
                      <div className={styles.rowSymbol}>:</div>
                    </>
                  )}
                  <div className={styles.rowVolumn}>
                    {`${formatAmount(web3BBSOLAmount)} bbSOL`}
                  </div>
                  <div className={styles.rowLine}></div>
                  <div
                    className={styles.rowPoints}
                    onClick={async () => {
                      const webUrl = `${window.location.origin}/${locale}/web3/staking/BybitSOL?utm_content=aidol2025`;
                      const deeplink = bbSOLDeepLink;

                      scTrack(WEB3_EVENT.Web3PageView, {
                        page_name: pageName,
                        spmName: projectName,
                        button_name: 'bbSOLWeb3',
                      });

                      hybirdOpen(deeplink, webUrl);
                    }}
                  >
                    {fm('amountPoints', {
                      amount: formatAmount(`${web3BBSOLPoints}`),
                    })}
                    <DirectionrightIcon color="#80F9F8" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div
          className={cls(styles.line, {
            [styles.profileLine]: profileStyle,
          })}
        ></div>
        <div className={styles.titleArea}>
          <div
            className={cls(styles.title, {
              [styles.profileTitle]: profileStyle,
            })}
          >
            {fm('getVotingPointsBybitAccount')}
          </div>
          <HelpIcon
            className={cls(styles.tipIcon, {
              [styles.profileTipIcon]: profileStyle,
            })}
            onClick={() => {
              scTrack(WEB3_EVENT.Web3UserClick, {
                page_name: pageName,
                spmName: projectName,
                button_name: 'HowtoGetVtoingPoints',
              });

              setShowModal(true);
            }}
          />
        </div>
        <div
          className={cls(styles.content, {
            [styles.profileContent]: profileStyle,
          })}
        >
          {userStatus === UserStatus.SPOT_NOT_BINDING && (
            <div className={styles.bindingArea}>
              <div
                className={styles.binding}
                onClick={() => {
                  if (!email) {
                    openSpotLoginModal();
                    return;
                  }

                  if (isBindingLoading) return;

                  beginBinding();
                }}
              >
                {isBindingLoading && <LoadingIcon className={styles.loading} />}
                {fm('connectBybitMainAccountTip').replace(/[!|！]/g, '')}
                <DirectionrightIcon className={styles.bindingArrow} />
              </div>
            </div>
          )}
          {(userStatus === UserStatus.WALLET_NOT_BINDING ||
            userStatus === UserStatus.SPOT_FINISH ||
            userStatus === UserStatus.WALLET_FINISH) && (
            <>
              {/* web3 dex */}
              <div className={styles.row}>
                <div className={styles.rowTitle}>{fm('spotTrading')}</div>
                <div className={styles.rowDetail}>
                  {!isMobile && (
                    <>
                      <div className={styles.rowDesc}>
                        {fm('tradingVolume')}
                      </div>
                      <div className={styles.rowSymbol}>:</div>
                    </>
                  )}
                  <div className={styles.rowVolumn}>
                    {`$${formatAmount(spotDexProAmount)}`}
                  </div>
                  <div className={styles.rowLine}></div>
                  <div
                    className={styles.rowPoints}
                    onClick={async () => {
                      const webUrl = `${window.location.origin}/${locale}/trade/spot/BTC/USDT`;
                      const deeplink = `bybitapp://open/home?tab=3&symbol=BTCUSDT`;

                      scTrack(WEB3_EVENT.Web3PageView, {
                        page_name: pageName,
                        spmName: projectName,
                        button_name: 'SpotTrade',
                      });

                      hybirdOpen(deeplink, webUrl);
                    }}
                  >
                    {fm('amountPoints', {
                      amount: formatAmount(`${spotDexProPoints}`),
                    })}
                    <DirectionrightIcon color="#80F9F8" />
                  </div>
                </div>
              </div>
              {/* web3 bbsol */}
              <div
                className={cls(styles.row, styles.secondRow, {
                  [styles.profileSecondRow]: profileStyle,
                })}
              >
                <div className={styles.rowTitle}>{fm('bbSolHolding')}</div>
                <div className={styles.rowDetail}>
                  {!isMobile && (
                    <>
                      <div className={styles.rowDesc}>{fm('holding')}</div>
                      <div className={styles.rowSymbol}>:</div>
                    </>
                  )}
                  <div className={styles.rowVolumn}>
                    {`${formatAmount(spotBBSOLAmount)} bbSOL`}
                  </div>
                  <div className={styles.rowLine}></div>
                  <div
                    className={styles.rowPoints}
                    onClick={async () => {
                      const webUrl = `${window.location.origin}/${locale}/trade/spot/BBSOL/USDT`;
                      const deeplink = `bybitapp://open/home?tab=3&symbol=BBSOLUSDT`;

                      scTrack(WEB3_EVENT.Web3PageView, {
                        page_name: pageName,
                        spmName: projectName,
                        button_name: 'bbSOLSpot',
                      });

                      hybirdOpen(deeplink, webUrl);
                    }}
                  >
                    {fm('amountPoints', {
                      amount: formatAmount(`${spotBBSOLPoints}`),
                    })}
                    <DirectionrightIcon color="#80F9F8" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <NoSSR>
        <LearnModal
          show={showModal}
          onClose={() => {
            setShowModal(false);
          }}
        />
      </NoSSR>
    </>
  );
};
